import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import moment from 'moment-timezone';
import DoctorServicePatientsView from '../../components/doctor_service/DoctorServicePatientsView';
import { apiCall, getAgendaCategoryByRole } from '../../utils';

const DoctorDashboard = ({ filterData }) => {
  const [dashboardData, setDashboardData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  const hasDiv = document.getElementById('js-doctor-service-dashboard-graph-content');

  const updateDashboard = () => {
    if (!hasDiv)
      return;
    setLoading(true);
    apiCall({
      method: 'GET',
      endpoint: 'api/v1/employee/agenda_report',
      data: {
        report: 'DOCTOR_SERVICE_DASHBOARD',
        startDate: moment(filterData.startDate).format('YYYY-MM-DD'),
        endDate: moment(filterData.endDate).format('YYYY-MM-DD')
      },
      successCallback: function(res) {
        setDashboardData(res.data);
        setLoading(false);
      },
      errorCallback: function() {
        setLoading(false);
      }
    });
  }

  React.useEffect(() => {
    updateDashboard();
  }, []);

  React.useEffect(() => {
    updateDashboard();
  }, [filterData]);

  React.useEffect(() => {
    if (!dashboardData) return;
    // Limpa gráficos antigos
    [
      'chart-tipo-paciente',
      'chart-genero',
      'chart-atendimentos-realizados',
    ].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = '';
    });

    // Dados
    const pn = dashboardData.PN || [];
    const pr = dashboardData.PR || [];
    const totalPacientes = pn.length + pr.length;
    const totalAgendadosConfirmadosPresentesEmAtendimento = [...pn, ...pr].filter(p => p.CATEGORIA === 'Agendado' || p.CATEGORIA === 'Confirmado' || p.CATEGORIA === 'Presente' || p.CATEGORIA === 'Em Atendimento').length;
    const totalPrevistas = totalAgendadosConfirmadosPresentesEmAtendimento;
    const totalRealizadas = [...pn, ...pr].filter(p => p.CATEGORIA === 'Atendido').length;
    const totalGeneroNaoInformado = [...pn, ...pr].filter(p => p.SEXO === null && (p.CATEGORIA === 'Agendado' || p.CATEGORIA === 'Confirmado' || p.CATEGORIA === 'Presente' || p.CATEGORIA === 'Em Atendimento')).length;
    const totalGeneroMasculino = [...pn, ...pr].filter(p => p.SEXO === 'M' && (p.CATEGORIA === 'Agendado' || p.CATEGORIA === 'Confirmado' || p.CATEGORIA === 'Presente' || p.CATEGORIA === 'Em Atendimento')).length;
    const totalGeneroFeminino = [...pn, ...pr].filter(p => p.SEXO === 'F' && (p.CATEGORIA === 'Agendado' || p.CATEGORIA === 'Confirmado' || p.CATEGORIA === 'Presente' || p.CATEGORIA === 'Em Atendimento')).length;

    // Gráfico tipo de paciente
    if (typeof ApexCharts !== 'undefined') {
      new ApexCharts(document.querySelector('#chart-tipo-paciente'), {
        chart: { type: 'pie', height: 150 },
        labels: ['Primeira vez', 'Seguimento'],
        series: [pn.length, pr.length],
        legend: { show: false },
        colors: ['#4fd1c5', '#63b3ed'],
        dataLabels: { enabled: true },
      }).render();

      // Gráfico gênero (tudo não informado)
      new ApexCharts(document.querySelector('#chart-genero'), {
        chart: { type: 'pie', height: 150 },
        labels: ['Não informado', 'Masculino', 'Feminino'],
        series: [totalGeneroNaoInformado, totalGeneroMasculino, totalGeneroFeminino],
        legend: { show: false },
        colors: ['#a0aec0'],
        dataLabels: { enabled: true },
      }).render();

      // Gráfico atendimentos realizados
      new ApexCharts(document.querySelector('#chart-atendimentos-realizados'), {
        chart: { type: 'donut', height: 150 },
        labels: ['Atendimentos Realizados', 'Restantes'],
        series: [totalRealizadas, totalAgendadosConfirmadosPresentesEmAtendimento - totalRealizadas],
        legend: { show: false },
        colors: ['#4fd1c5', '#e2e8f0'],
        dataLabels: { enabled: true },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Realizados',
                  formatter: function() {
                    return totalAgendadosConfirmadosPresentesEmAtendimento === 0 ? '0%' : Math.round((totalRealizadas / totalAgendadosConfirmadosPresentesEmAtendimento) * 100) + '%';
                  }
                }
              }
            }
          }
        }
      }).render();
    }
  }, [dashboardData]);

  // Cards
  let pn = dashboardData?.PN || [];
  let pr = dashboardData?.PR || [];
  let totalPacientes = pn.length + pr.length;
  let totalAgendadosConfirmadosPresentesEmAtendimento = [...pn, ...pr].filter(p => p.CATEGORIA === 'Agendado' || p.CATEGORIA === 'Confirmado' || p.CATEGORIA === 'Presente' || p.CATEGORIA === 'Em Atendimento').length;
  let totalPrevistas = totalAgendadosConfirmadosPresentesEmAtendimento;
  let totalRealizadas = [...pn, ...pr].filter(p => p.CATEGORIA === 'Atendido').length;
  let totalNaoCompareceu = [...pn, ...pr].filter(p => p.CATEGORIA === 'Não Compareceu').length;

  return (
    !hasDiv ? null : (
    <div className="container-fluid p-0 m-0">
      {loading ? (
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : (
        dashboardData && (
          <>
            <button className="btn btn-primary mb-3" onClick={updateDashboard}>
              <i className="fas fa-sync-alt"></i> Atualizar
            </button>
            {/* Linha de totais principais */}
            <div className="row mb-3">
              <div className="col-md-6 mb-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <div className="h2 mb-1">{totalAgendadosConfirmadosPresentesEmAtendimento}</div>
                    <div className="text-muted">pacientes do dia ({moment(filterData.startDate).format('DD/MM/YYYY')})</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <div className="h2 mb-1">{totalPrevistas}/{totalRealizadas}</div>
                    <div className="text-muted">atendimentos previstos / realizados</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha de gráficos */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div id="chart-tipo-paciente" style={{height: 150}}></div>
                    <div className="text-center mt-2 small">tipo de paciente</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div id="chart-genero" style={{height: 150}}></div>
                    <div className="text-center mt-2 small">pacientes por gênero</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div id="chart-atendimentos-realizados" style={{height: 150}}></div>
                    <div className="text-center mt-2 small">atendimentos realizados</div>
                  </div>
                </div>
              </div>
            </div>
          </>
          )
        )}
      </div>
    )
  );
}

const DoctorService = () => {

  let isFirstOrSecondDayOfMonth = moment().date() === 1 || moment().date() === 2,
      rangeStartDate = moment(),
      rangeEndDate = moment();

  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBusinessBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedBranchName, setSelectedBranchName] = useState(null);
  const [currentXhr, setCurrentXhr] = useState(false);
  const [businessPlans, setBusinessPlans] = useState('');
  const [businessServices, setBusinessServices] = useState('');
  const [branchDoctors, setBranchDoctors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(2);
  const [filterData, setFilterData] = useState({
    startDate: rangeStartDate.format('YYYY-MM-DD 00:00:00.000'),
    endDate: rangeEndDate.format('YYYY-MM-DD 00:00:00.000')
  })
  const interval = useRef(null);

  const initData = () => {
    let userProfileResponse = { ...window._sessionData };
    if (!Array.isArray(userProfileResponse.businessBranches)) {
      userProfileResponse.businessBranches = Object.keys(userProfileResponse.businessBranches).map((key) => {
        return userProfileResponse.businessBranches[key];
      });
    }
    setBusinessBranches(userProfileResponse.businessBranches);
    clearInterval(interval.current);

    $('.js-doctor-service-categories').select2();
    //let selectedOptions = $('.js-doctor-service-categories').val();
    //setSelectedCategories(selectedOptions);

    // get selected options from select2
    $('.js-doctor-service-categories').on('change', function() {
      let selectedOptions = $(this).val();
      setCurrentCategory(0);
      setSelectedCategories(selectedOptions);
    });

    $('.js-business-branches-selector').on('change', function() {
      let selectedBranch = $(this).val();
      setSelectedBranch(selectedBranch);
      setSelectedBranchName($('.js-doctor-agenda-branches-selector option:selected').text());
    });

    $('.js-doctor-service-form').submit(function(e) {
      e.preventDefault();
      //console.log('submit', 'submit');
    });
    
    $('input[name="daterange"]').daterangepicker({
        opens: 'left',
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        opens: "right",
        drops: "down",
        locale: window._sessionData.i8nDateRangePicker, 
        ranges: {
            'Hoje': [moment().startOf('day'), moment().endOf('day')],
            'Ontem': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
            'Amanhã': [moment().add(1, 'days'), moment().add(1, 'days')],
            'Últimos 7 Dias': [moment().subtract(6, 'days'), moment()],
            'Últimos 30 Dias': [moment().subtract(29, 'days'), moment()],
            'Este Mês': [moment().startOf('month'), moment().endOf('month')],
            'Último Mês': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
        },
    }, function(start, end, label) {
        //console.log('setFilterData', 'setFilterData');
        setFilterData({
          startDate: start.format('YYYY-MM-DD 00:00:00.000'),
          endDate: end.format('YYYY-MM-DD 00:00:00.000')
        })
    });

    window.doctorsReadyFunctions.push(function(data) {
      setBranchDoctors(window.businessBranchDoctors);
    });

    // Resgata lista de planos
    apiCall({
      method: 'GET',
      endpoint: 'api/v1/business/plans',
      successCallback: function(res) {
        if (!res.error) {
          setBusinessPlans(res.data);
        }
      },
      errorCallback: function(err) {
      }
    });

    // Resgata lista de serviços
    apiCall({
      method: 'GET',
      endpoint: 'api/v1/business/agenda_services',
      successCallback: function(res) {
        if (!res.error) {
          setBusinessServices(res.data);
        }
      },
      errorCallback: function(err) {
      }
    });
  }
  
  React.useEffect(() => {
    interval.current = setInterval(() => {
      if (window._sessionData.businessBranches && branches.length === 0) {
        let CategoryAgendado = getAgendaCategoryByRole('AGENDADO'),
            CategoryAtendido = getAgendaCategoryByRole('ATENDIDO'),
            CategoryPresente = getAgendaCategoryByRole('PRESENTE'),
            CategoryEmAtendimento = getAgendaCategoryByRole('EM_ATENDIMENTO'),
            CategoryNaoCompareceu = getAgendaCategoryByRole('NAO_COMPARECEU'),
            CategoryCancelado = getAgendaCategoryByRole('CANCELADO'),
            CategoryReagendado = getAgendaCategoryByRole('REAGENDADO'),
            CategoryConfirmado = getAgendaCategoryByRole('CONFIRMADO');
        setSelectedCategories([
          `${CategoryAgendado} / ${CategoryConfirmado}`,
          CategoryAtendido,
          `${CategoryPresente} / ${CategoryEmAtendimento}`,
          CategoryNaoCompareceu,
          CategoryCancelado,
          CategoryReagendado
        ]);
        initData();
      }
    }, 1000);
  }, []);

  useEffect(() => {
    document.addEventListener('click', function(e) {
      handleNavLinkClick(e);
    });

    return () => {
      document.removeEventListener('click', function (e) {
        handleNavLinkClick(e);
      });
    }
  }, []);

  const handleNavLinkClick = (e) => {
    if (e.target.classList.contains('nav-link')) {
      let categoryIdx = parseInt(e.target.getAttribute('data-category-idx'));
      setCurrentCategory(categoryIdx);
    }
  }

  const getBranchDoctorFromStaffId = (staffId) => {
    let branchDoctor = null;
    staffId = parseInt(staffId);
    branchDoctors.forEach((doctor) => {
      if (parseInt(doctor.STAFF_ID) === staffId) {
        branchDoctor = doctor.PRIMEIRO_NOME;
      }
    });
    return branchDoctor;
  }

  return (
    <>
    <>
      <DoctorDashboard filterData={filterData} />
    </>
    <div class="row"> 
      <div class="col-12">
          <div class="mb-3 card">
              <div class="tab-content p-2">
                  <div class="d-flex justify-content-center">
                      <form method="POST" class="js-doctor-service-form w-100 w-md-50">
                          <input type="hidden" name="action" value="filter" />
                          <div class="filter-container">
                              <div class="form-group mb-3">
                                  <label>Filial</label>
                                  <select id="cityFilter" name="branch" class="form-select js-business-branches-selector js-doctor-agenda-branches-selector">
                                  </select>
                              </div>
                              <div class="form-group mb-3">
                                  <label>Período do relatório</label>
                                  <input class="form-control" type="text" name="daterange" value={moment(filterData.startDate).format('DD/MM/YYYY')} />
                              </div>
                              <div class="form-group mb-3 d-none">
                                  <label>Categoria</label>
                                  <select multiple class="form-select js-doctor-service-categories form-control">
                                      <option value="Agendado">Agendado</option>
                                      <option value="Atendido">Atendido</option>
                                      <option value="Presente" selected>Presente</option>
                                      <option value="Não Compareceu">Não Compareceu</option>
                                      <option value="Cancelado">Cancelado</option>
                                      <option value="Reagendado">Reagendado</option>
                                      <option value="Confirmado">Confirmado</option>
                                  </select>
                              </div>
                          </div>
                      </form>
                  </div>
               
                  {selectedBranch == null && branches.length > 0 ? (
                    <div class="tab-content p-2 py-5">
                      <div className="alert alert-warning" role="alert">
                        <strong>Atenção!</strong><br />Selecione uma filial para visualizar seus atendimentos.
                      </div>
                    </div>
                  ) : (
                    <>
                      <h6>Categorias</h6>
                      <ul className="nav nav-pills mb-3" id="pills-tab" role="tablist">
                        {selectedCategories.length > 0 && selectedCategories.map((category, index) => {
                          return (
                            <li key={`opts-${index + 1}`} className={`nav-item`} role="presentation">
                              <button className={`nav-link ${currentCategory === index ? 'active' : ''}`} id={category + '-tab'} data-category-idx={index} data-bs-toggle="pill" data-bs-target={`#${category}`} type="button" role="tab" aria-controls={category} aria-selected={currentCategory === index ? 'true' : 'false'}>{category}</button>
                            </li>
                          )
                        })}
                      </ul>
                      <div className="tab-content" id="pills-tabContent">
                        {selectedCategories.length > 0 && selectedCategories.map((category, index) => {
                          return <div key={`tabcontent-${index + 1}`} className={`tab-pane fade ${currentCategory === index ? 'show active' : ''}`} id={category} role="tabpanel" aria-labelledby={category + '-tab'} tabIndex={index + 1}>
                            <DoctorServicePatientsView 
                              key={`tabcontainer-${index + 1}`} 
                              isTabOpen={currentCategory === index ? true : false} 
                              branchId={selectedBranch} 
                              branchName={selectedBranchName}
                              category={category} 
                              filterData={filterData} 
                              currentXhr={currentXhr} 
                              setCurrentXhr={setCurrentXhr} 
                              businessPlans={businessPlans}
                              businessServices={businessServices}
                              getBranchDoctorFromStaffId={getBranchDoctorFromStaffId}
                              />
                          </div>
                        })}
                      </div>
                    </>
                  )}
                  </div>
              </div>
          </div>
      </div>
    </>
  )
};

try {
  if (document.getElementById('js-doctor-service-dashboard-content')) {
    const container = document.getElementById('js-doctor-service-dashboard-content');
    const root = createRoot(container); // createRoot(container!) if you use TypeScript
    root.render(<DoctorService />);
  }
} catch(err) {
  console.error('Error rendering DoctorService: ', err);
}